<template>
  <div class="wysiwyg-editor">
    <!-- Toolbar -->
    <div class="editor-toolbar">
      <button 
        type="button" 
        class="toolbar-btn" 
        :class="{ 'active': isActive('bold') }"
        title="Negrita"
        @click="execCommand('bold')"
      >
        <i class="material-icons">format_bold</i>
      </button>
      <button 
        type="button" 
        class="toolbar-btn" 
        :class="{ 'active': isActive('italic') }"
        title="Cursiva"
        @click="execCommand('italic')"
      >
        <i class="material-icons">format_italic</i>
      </button>
      <div class="toolbar-separator"></div>
      <button 
        type="button" 
        class="toolbar-btn" 
        :class="{ 'active': isActive('insertOrderedList') }"
        title="Lista ordenada"
        @click="execCommand('insertOrderedList')"
      >
        <i class="material-icons">format_list_numbered</i>
      </button>
      <button 
        type="button" 
        class="toolbar-btn" 
        :class="{ 'active': isActive('insertUnorderedList') }"
        title="Lista con viñetas"
        @click="execCommand('insertUnorderedList')"
      >
        <i class="material-icons">format_list_bulleted</i>
      </button>
      <div class="toolbar-separator"></div>
      <button 
        type="button" 
        class="toolbar-btn" 
        title="Insertar enlace"
        @click="insertLink"
      >
        <i class="material-icons">link</i>
      </button>
      <button 
        type="button" 
        class="toolbar-btn" 
        title="Insertar imagen"
        @click="triggerImageSelect"
      >
        <i class="material-icons">image</i>
      </button>
      <button 
        type="button" 
        class="toolbar-btn" 
        title="Limpiar formato"
        @click="execCommand('removeFormat')"
      >
        <i class="material-icons">format_clear</i>
      </button>
    </div>
    
    <!-- Editable Content Area -->
    <div 
      class="editor-content" 
      contenteditable="true"
      ref="editorContent"
      @input="updateContent"
      @paste="handlePaste"
      @mouseup="checkActiveButtons"
      @keyup="checkActiveButtons"
    ></div>
    
    <!-- Hidden textarea for form submission -->
    <textarea 
      :name="name" 
      v-model="content" 
      style="display: none;"
    ></textarea>
    <input type="file" ref="imageInput" @change="handleImageChange" accept="image/*" style="display:none" />
  </div>
</template>

<script>
export default {
  name: 'WysiwygEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: 'content'
    },
    placeholder: {
      type: String,
      default: 'Escribe tu contenido aquí...'
    }
  },
  data() {
    return {
      content: '',
      isInitialized: false,
      activeCommands: {
        bold: false,
        italic: false,
        insertOrderedList: false,
        insertUnorderedList: false
      },
      uploadingImage: false
    };
  },
  watch: {
    value: {
      handler(newVal) {
        // Only update the editor content if it's different from current content
        // and if it's not the initial setup
        if (!this.isInitialized && newVal) {
          this.setContent(newVal);
          this.isInitialized = true;
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Set placeholder if content is empty
    if (!this.value) {
      this.$refs.editorContent.dataset.placeholder = this.placeholder;
    }
    
    // Set initial content
    this.setContent(this.value);
    this.isInitialized = true;
    
    // Add event listener for selection changes
    document.addEventListener('selectionchange', this.checkActiveButtons);
  },
  beforeDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener('selectionchange', this.checkActiveButtons);
  },
  methods: {
    execCommand(command, value = null) {
      // Focus the editor first
      this.$refs.editorContent.focus();
      
      // Execute the command
      document.execCommand(command, false, value);
      
      // Update the content after command execution
      this.updateContent();
      
      // Check which buttons should be active
      this.checkActiveButtons();
    },
    
    updateContent() {
      // Get the HTML content from the editable div
      const html = this.$refs.editorContent.innerHTML;
      
      // Update the content data property
      this.content = html;
      
      // Emit input event for v-model
      this.$emit('input', html);
    },
    
    setContent(html) {
      // Set the HTML content of the editable div
      if (this.$refs.editorContent) {
        this.$refs.editorContent.innerHTML = html;
        this.content = html;
      }
    },
    
    insertLink() {
      const url = prompt('Introduce la URL del enlace:', 'http://');
      if (url) {
        this.execCommand('createLink', url);
      }
    },
    
    handlePaste(e) {
      // Prevent the default paste behavior
      e.preventDefault();
      
      // Get text from clipboard
      const text = (e.originalEvent || e).clipboardData.getData('text/plain');
      
      // Insert text at cursor position
      document.execCommand('insertText', false, text);
    },
    
    // Check which formatting options are active for the current selection
    checkActiveButtons() {
      // Only check if the editor has focus
      if (!document.activeElement || !this.$refs.editorContent.contains(document.activeElement)) {
        return;
      }
      
      // Check each command
      this.activeCommands.bold = document.queryCommandState('bold');
      this.activeCommands.italic = document.queryCommandState('italic');
      this.activeCommands.insertOrderedList = document.queryCommandState('insertOrderedList');
      this.activeCommands.insertUnorderedList = document.queryCommandState('insertUnorderedList');
    },
    
    // Helper method to check if a command is active
    isActive(command) {
      return this.activeCommands[command];
    },
    
    // Method to get plain text content (useful for validation)
    getPlainText() {
      const div = document.createElement('div');
      div.innerHTML = this.content;
      return div.textContent || div.innerText || '';
    },
    triggerImageSelect() {
      if (this.uploadingImage) return;
      this.$refs.imageInput.click();
    },
    handleImageChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.uploadingImage = true;
      const formData = new FormData();
      formData.append('image', file);
      // Use global axios instance if available
      const axiosInstance = (this.$root && this.$root.$axios) ? this.$root.$axios : (window.axios || null);
      const axios = axiosInstance ? axiosInstance : require('axios');
      axios.post('/forum/api/image/upload', formData)
        .then(res => {
          if (res.data && res.data.success && res.data.url) {
            // Insert the image at the cursor position with max size constraints
            this.insertImageWithSizeConstraints(res.data.url);
          } else {
            alert(res.data.message || 'Error subiendo la imagen');
          }
        })
        .catch(err => {
          console.error('Image upload failed', err);
          alert('Error subiendo la imagen');
        })
        .finally(() => {
          this.uploadingImage = false;
          this.$refs.imageInput.value = '';
        });
    },
    
    insertImageWithSizeConstraints(imageUrl) {
      // Create a temporary image to get dimensions
      const tempImg = new Image();
      tempImg.onload = () => {
        // Calculate the display size while respecting max constraints
        const maxWidth = 600;
        const maxHeight = 400;
        
        let displayWidth = tempImg.naturalWidth;
        let displayHeight = tempImg.naturalHeight;
        
        // Scale down if image is larger than max constraints
        if (displayWidth > maxWidth || displayHeight > maxHeight) {
          const widthRatio = maxWidth / displayWidth;
          const heightRatio = maxHeight / displayHeight;
          const ratio = Math.min(widthRatio, heightRatio);
          
          displayWidth = Math.round(displayWidth * ratio);
          displayHeight = Math.round(displayHeight * ratio);
        }
        
        // Insert the image with size constraints
        const imageHtml = `<img src="${imageUrl}" style="max-width: ${maxWidth}px; max-height: ${maxHeight}px; width: ${displayWidth}px; height: ${displayHeight}px; object-fit: contain; border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); margin: 0.5rem 0;" alt="Uploaded image" />`;
        
        // Use execCommand to insert the HTML at cursor position
        if (document.queryCommandSupported('insertHTML')) {
          this.execCommand('insertHTML', imageHtml);
        } else {
          // Fallback for browsers that don't support insertHTML
          this.execCommand('insertImage', imageUrl);
          // Try to apply styling to the inserted image
          this.$nextTick(() => {
            const insertedImages = this.$refs.editorContent.querySelectorAll('img');
            const lastImage = insertedImages[insertedImages.length - 1];
            if (lastImage && lastImage.src === imageUrl) {
              lastImage.style.maxWidth = `${maxWidth}px`;
              lastImage.style.maxHeight = `${maxHeight}px`;
              lastImage.style.width = `${displayWidth}px`;
              lastImage.style.height = `${displayHeight}px`;
              lastImage.style.objectFit = 'contain';
              lastImage.style.borderRadius = '8px';
              lastImage.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              lastImage.style.margin = '0.5rem 0';
            }
          });
        }
        
        // Update the content
        this.updateContent();
      };
      
      tempImg.onerror = () => {
        // If image loading fails, insert without size constraints as fallback
        this.execCommand('insertImage', imageUrl);
        this.updateContent();
      };
      
      tempImg.src = imageUrl;
    }
  }
};
</script>

<style scoped>
.wysiwyg-editor {
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  background-color: var(--color-background);
  overflow: hidden;
}

.editor-toolbar {
  display: flex;
  padding: 8px;
  background-color: var(--color-active-surface);
  border-bottom: 1px solid var(--color-outline);
  flex-wrap: wrap;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  border-radius: 4px;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.toolbar-btn:hover {
  background-color: var(--color-background);
}

.toolbar-btn:active {
  background-color: var(--color-highlight-light, rgba(33, 150, 243, 0.1));
}

/* Style for active buttons */
.toolbar-btn.active {
  background-color: var(--color-highlight-light, rgba(33, 150, 243, 0.2));
  color: var(--color-highlight);
}

.toolbar-separator {
  width: 1px;
  height: 24px;
  background-color: var(--color-outline);
  margin: 0 8px;
}

.editor-content {
  min-height: 200px;
  padding: 12px;
  color: var(--color-text-primary);
  overflow-y: auto;
  line-height: 1.5;
}

/* Placeholder styling */
.editor-content[contenteditable=true]:empty:before {
  content: attr(data-placeholder);
  color: var(--color-text-secondary);
  opacity: 0.7;
  cursor: text;
}

/* Style for lists inside the editor */
.editor-content ul, .editor-content ol {
  padding-left: 24px;
  margin: 8px 0;
}

.editor-content li {
  margin-bottom: 4px;
}

/* Style for links inside the editor */
.editor-content a {
  color: var(--color-highlight);
  text-decoration: underline;
}

/* Style for images inside the editor */
.editor-content img {
  max-width: 600px;
  max-height: 400px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0.5rem 0;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.editor-content img:hover {
  transform: scale(1.02);
}

/* Focus state */
.editor-content:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-highlight-light, rgba(33, 150, 243, 0.2));
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .toolbar-btn {
    width: 32px;
    height: 32px;
  }
  
  .toolbar-separator {
    height: 20px;
    margin: 0 4px;
  }
  
  /* Make images more responsive on mobile in editor */
  .editor-content img {
    max-width: 100%;
    max-height: 300px;
  }
}
</style> 